// ═══════════════════════════════════════════════════════════════════════════
// Vendedores — la capa de datos
//
// Todo pasa por funciones de Postgres, no por consultas sueltas: quién puede
// invitar, quién puede fijar una comisión y quién puede dar de baja se decide
// en la base. Aquí no hay ninguna comprobación de permisos, a propósito —
// duplicarla en el navegador daría la falsa impresión de que sirve de algo.
//
// A diferencia del catálogo, esto NO va por la bitácora offline. Invitar a
// alguien o cambiarle el esquema de pago son actos de administración que se
// hacen sentado, con red, y que necesitan respuesta inmediata: un ticket que
// se genera "en algún momento" no sirve para dárselo a nadie.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "../auth/sesion.js";

async function rpc(nombre, args) {
  const { data, error } = await supabase.rpc(nombre, args);
  if (error) throw new Error(error.message);
  return data;
}

// ── Lectura ────────────────────────────────────────────────────────────────

export const equipo = (tenantId) => rpc("equipo", { p_tenant: tenantId });

export const invitacionesPendientes = (tenantId) =>
  rpc("invitaciones_pendientes", { p_tenant: tenantId });

// `resumenCartera` vivía aquí y se fue con la vista `cartera_resumen` (0043).
// Ningún componente la importaba —se buscó en todo el repo— y la vista que leía
// era la que Supabase marcó como crítica: corría saltándose la RLS y sacaba
// correos de `auth.users` a la superficie pública de la API, para nadie.
// Lo que sí se usa para la pantalla del equipo es `equipo()`, que va por RPC y
// lleva `es_gerencia()` adentro.

// ── Alta y baja ────────────────────────────────────────────────────────────

/**
 * Genera un ticket de un solo uso.
 * @returns { codigo, expira }
 */
export const crearInvitacion = (tenantId, { email, rol = "agent",
                                            esquema = "comision", dias = 7 } = {}) =>
  rpc("crear_invitacion", {
    p_tenant: tenantId,
    p_email: email || null,
    p_role: rol,
    p_scheme: esquema,
    // El ticket NO lleva tasa: se pone por cliente al asignar cartera. Ver
    // migración 0017 — una tasa por omisión convierte un descuido en dinero.
    p_dias: dias,
  });

export const revocarInvitacion = (tenantId, codigo) =>
  rpc("revocar_invitacion", { p_tenant: tenantId, p_codigo: codigo });

/** Lo llama quien acaba de iniciar sesión y todavía no pertenece a nada. */
export const consumirInvitacion = (codigo) =>
  rpc("consumir_invitacion", { p_codigo: codigo });

export const darDeBaja = (tenantId, membershipId, motivo) =>
  rpc("dar_de_baja", { p_tenant: tenantId, p_membership: membershipId, p_motivo: motivo });

export const fijarEsquema = (tenantId, membershipId, esquema, motivo) =>
  rpc("fijar_esquema", { p_tenant: tenantId, p_membership: membershipId,
                         p_scheme: esquema, p_motivo: motivo });

// ── Cartera y comisiones ───────────────────────────────────────────────────

export const asignarCartera = (tenantId, legacyIds, membershipId, motivo) =>
  rpc("asignar_cartera", { p_tenant: tenantId, p_clientes: legacyIds,
                           p_membership: membershipId, p_motivo: motivo, p_pct: null });

export const fijarComision = (tenantId, legacyIds, pct, motivo) =>
  rpc("fijar_comision", { p_tenant: tenantId, p_clientes: legacyIds,
                          p_pct: pct, p_motivo: motivo });

// ── El perfil de la persona ────────────────────────────────────────────────

export async function miPerfil(userId) {
  const { data, error } = await supabase
    .from("person_profile").select("*").eq("user_id", userId).maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

export async function guardarMiPerfil(userId, campos) {
  const { data, error } = await supabase
    .from("person_profile")
    .upsert({ user_id: userId, ...campos }, { onConflict: "user_id" })
    .select().single();
  if (error) throw new Error(error.message);
  return data;
}

/** El CLABE son 18 dígitos. Se valida aquí para avisar antes de ir al servidor. */
export function clabeValido(v) {
  return /^[0-9]{18}$/.test(String(v || "").replace(/\s/g, ""));
}

/**
 * Los clientes con lo que hace falta para repartir cartera.
 *
 * Se lee de Postgres y no de la caché de la app: `assigned_agent_id` y
 * `commission_pct` no viajan en la forma que usa la interfaz, y esta es una
 * pantalla de administración que se usa sentado y con red. Traer datos viejos
 * aquí haría asignar sobre una foto equivocada.
 */
export async function clientesParaCartera(tenantId) {
  const { data, error } = await supabase
    .from("client")
    .select("legacy_id, name, city, state, rfc, assigned_agent_id, commission_pct")
    .eq("tenant_id", tenantId)
    .is("deleted_at", null)
    .order("name");
  if (error) throw new Error(error.message);
  return data || [];
}

// ── Comisiones ─────────────────────────────────────────────────────────────

/** Lo mío, en TODAS las vinícolas donde trabajo. */
export const misComisiones = () => rpc("mis_comisiones", {});

/** El detalle remisión por remisión, para poder revisar de dónde sale. */
export async function detalleComisiones(tenantId, membershipId) {
  const { data, error } = await supabase
    .from("comision_remision")
    .select("folio, issued_at, cliente, total, commission_pct, comision, exigible, estado_cobranza, pagada_al_vendedor")
    .eq("tenant_id", tenantId)
    .eq("membership_id", membershipId)
    .order("issued_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

/** Los depósitos que ya se le hicieron. */
export async function pagosDeComision(tenantId, membershipId) {
  const { data, error } = await supabase
    .from("commission_payment")
    .select("amount, occurred_at, method, reference, note")
    .eq("tenant_id", tenantId)
    .eq("membership_id", membershipId)
    .order("occurred_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

/** Lo que falta pagarle: exigible y sin cubrir. Es lo que se selecciona. */
export const comisionesPorPagar = (tenantId, membershipId) =>
  rpc("comisiones_por_pagar", { p_tenant: tenantId, p_membership: membershipId });

/**
 * Registrar un depósito. Solo gerencia.
 *
 * El monto NO se manda: sale de las remisiones elegidas. `monto` solo se usa
 * para un anticipo a cuenta, que es la excepción.
 */
export const pagarComision = (tenantId, membershipId, remisionIds, extra = {}) =>
  rpc("pagar_comision", {
    p_tenant: tenantId, p_membership: membershipId,
    p_remisiones: remisionIds?.length ? remisionIds : null,
    p_monto: extra.montoACuenta || null,
    p_metodo: extra.metodo || null, p_referencia: extra.referencia || null,
    p_nota: extra.nota || null, p_fecha: extra.fecha || null,
  });

export const revertirPagoComision = (tenantId, pagoId, motivo) =>
  rpc("revertir_pago_comision", { p_tenant: tenantId, p_pago: pagoId, p_motivo: motivo });

/**
 * Cambiar de quién es una remisión.
 *
 * La base se niega si su comisión ya se pagó — eso se corrige revirtiendo el
 * pago, no reescribiendo a quién fue la venta. Aquí no se repite esa
 * comprobación: duplicarla daría la falsa impresión de que sirve de algo.
 */
export const asignarAgenteRemision = (remisionId, membershipId, motivo) =>
  rpc("asignar_agente_remision", {
    p_remision: remisionId, p_agente: membershipId || null, p_motivo: motivo || null,
  });
